// The compass strip (decision 0048: the engine supports both a minimap and a compass strip on screen during play; the
// compass is the map's data drawn another way). Bearings are measured clockwise from map-up (north, −z on the ground,
// as the minimap draws it): east is +x. `compassMarks` is the pure placement (testable in Node); `createCompass` is a
// DOM strip written directly each frame (no Preact render), its marks placed by transform only when they move.
import { UI_FAULTS } from "./faults";

/** The bearing from (x, z) to (tx, tz): radians clockwise from north (−z), 0..2π. */
export function bearingTo(x: number, z: number, tx: number, tz: number): number {
  const b = Math.atan2(tx - x, -(tz - z));
  return b < 0 ? b + 2 * Math.PI : b;
}

/** An angle wrapped into (−π, π]. */
export function wrapAngle(a: number): number {
  const t = ((((a + Math.PI) % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI)) - Math.PI;
  return t === -Math.PI ? Math.PI : t;
}

/** A mark for the strip: an id, its bearing, and a label (the cardinals' letters, a place's icon). */
export interface CompassItem {
  id: string;
  bearing: number;
  label?: string;
  /** A styling hook (`data-tone`). */
  tone?: string;
}

/** Where a mark sits on a strip `width` px wide showing `fov` radians centred on `heading`. */
export interface CompassMark {
  id: string;
  /** Px from the strip's left edge. */
  x: number;
  /** The mark's angle off the heading, radians (negative: to the left). */
  off: number;
  visible: boolean;
}

/** Place marks on the strip: a mark straight ahead sits in the middle; one `fov`/2 to the right sits at the right
 *  edge; marks past the edges are not visible. */
export function compassMarks(heading: number, fov: number, width: number, items: readonly CompassItem[]): CompassMark[] {
  const half = fov / 2;
  return items.map((it) => {
    let off = wrapAngle(it.bearing - heading);
    if (UI_FAULTS.compassMirrored) off = -off;
    return { id: it.id, off, x: width / 2 + (off / half) * (width / 2), visible: Math.abs(off) <= half };
  });
}

/** The four cardinal points as compass items, labelled by the game's strings. */
export function cardinals(labels: { n: string; e: string; s: string; w: string }): CompassItem[] {
  return [
    { id: "N", bearing: 0, label: labels.n, tone: "cardinal" },
    { id: "E", bearing: Math.PI / 2, label: labels.e, tone: "cardinal" },
    { id: "S", bearing: Math.PI, label: labels.s, tone: "cardinal" },
    { id: "W", bearing: (3 * Math.PI) / 2, label: labels.w, tone: "cardinal" },
  ];
}

/** The strip. */
export interface Compass {
  readonly el: HTMLElement;
  /** Place the marks for this frame (direct writes). */
  frame(heading: number, items: readonly CompassItem[]): void;
  /** Each mark's last placement. */
  state(): CompassMark[];
  writes(): number;
}

const CSS = `
.rpg-compass{position:relative;width:min(44vw,26em);height:1.9em;border-radius:.95em;background:rgba(18,22,16,.55);overflow:hidden;
  -webkit-mask-image:linear-gradient(90deg,transparent,#000 12%,#000 88%,transparent);mask-image:linear-gradient(90deg,transparent,#000 12%,#000 88%,transparent)}
.rpg-compass::after{content:"";position:absolute;left:50%;top:0;bottom:0;width:2px;margin-left:-1px;background:rgba(251,246,232,.6)}
.rpg-compass i{position:absolute;left:0;top:50%;font-style:normal;font-weight:700;font-size:.85em;white-space:nowrap}
.rpg-compass i[hidden]{display:none}
.rpg-compass i[data-tone=cardinal]{color:#fbf6e8}
.rpg-compass i[data-tone=objective]{color:#e9c46a}
.rpg-compass i[data-tone=waypoint]{color:#9fd8ff}
`;

/** Build a compass strip `fov` radians wide (default 180°). */
export function createCompass(o: { fov?: number; doc?: Document } = {}): Compass {
  const doc = o.doc ?? document;
  if (doc.head && !doc.head.querySelector("style[data-rpg-compass]")) {
    const s = doc.createElement("style");
    s.dataset.rpgCompass = "";
    s.textContent = CSS;
    doc.head.appendChild(s);
  }
  const fov = o.fov ?? Math.PI;
  const el = doc.createElement("div");
  el.className = "rpg-compass";
  el.dataset.compass = "";
  const marks = new Map<string, { el: HTMLElement; x: number; shown: boolean | null; label: string }>();
  let last: CompassMark[] = [];
  let writes = 0;
  return {
    el,
    frame(heading, items) {
      const width = el.clientWidth || 300;
      last = compassMarks(heading, fov, width, items);
      const seen = new Set<string>();
      last.forEach((m, i) => {
        const it = items[i]!;
        seen.add(m.id);
        let mk = marks.get(m.id);
        if (!mk) {
          const e = doc.createElement("i");
          e.dataset.mark = m.id;
          if (it.tone) e.dataset.tone = it.tone;
          el.appendChild(e);
          mk = { el: e, x: Number.NaN, shown: null, label: "" };
          marks.set(m.id, mk);
        }
        const label = it.label ?? "";
        if (label !== mk.label) {
          mk.el.textContent = label;
          mk.label = label;
          writes++;
        }
        if (m.visible !== mk.shown) {
          mk.shown = m.visible;
          if (m.visible) mk.el.removeAttribute("hidden");
          else mk.el.setAttribute("hidden", "");
          writes++;
        }
        if (m.visible && !(Math.abs(m.x - mk.x) < 0.5)) {
          mk.el.style.transform = `translate(${m.x.toFixed(1)}px,-50%) translateX(-50%)`;
          mk.x = m.x;
          writes++;
        }
      });
      for (const [id, mk] of [...marks]) {
        if (seen.has(id)) continue;
        mk.el.remove();
        marks.delete(id);
      }
    },
    state: () => last.map((m) => ({ ...m })),
    writes: () => writes,
  };
}
